/**
 * Copies text to clipboard with fallback for environments where navigator.clipboard is not available
 * (e.g., non-HTTPS contexts, iframes with restricted permissions)
 *
 * @param text - The text to copy to clipboard
 * @returns Promise that resolves when text is successfully copied
 */
export const copyToClipboard = async (text: string): Promise<void> => {
  // Check if we're in a browser environment
  if (
    typeof window === 'undefined' ||
    typeof document === 'undefined' ||
    typeof navigator === 'undefined'
  ) {
    throw new Error(
      'Clipboard operations are only available in browser environments'
    )
  }

  // Try modern Clipboard API first
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text)
      return
    } catch (error) {
      console.warn('Clipboard API failed, trying fallback method:', error)
    }
  }

  // Fallback to legacy method using execCommand
  return new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('Document is not available'))
      return
    }

    const textArea = document.createElement('textarea')
    textArea.value = text

    // Make the textarea invisible and unobtrusive
    textArea.style.position = 'fixed'
    textArea.style.left = '-999999px'
    textArea.style.top = '-999999px'
    textArea.style.opacity = '0'
    textArea.setAttribute('readonly', '')

    document.body.appendChild(textArea)

    try {
      // Preserve scroll to avoid jump when focusing hidden textarea
      const scrollX = window.scrollX
      const scrollY = window.scrollY

      // Select the text
      try {
        textArea.focus({ preventScroll: true })
      } catch {
        // Fallback for browsers that do not support focus options
        textArea.focus()
      }
      textArea.select()

      // For mobile devices
      textArea.setSelectionRange(0, text.length)

      // Ensure viewport position remains unchanged after selection
      window.scrollTo(scrollX, scrollY)

      // Execute copy command
      const successful = document.execCommand('copy')

      if (successful) {
        resolve()
      } else {
        reject(new Error('Copy command was unsuccessful'))
      }
    } catch (error) {
      reject(error)
    } finally {
      document.body.removeChild(textArea)
    }
  })
}
